import type React from 'react'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
} from '@heroicons/react/24/outline'

import {
  DocumentSignatory,
  DocumentSigningTemplateBlock,
  DocumentSigningTemplateBlockType,
} from '../../../../lib/db/db.types'

import { BlockDisplay, PdfDisplay, PdfDisplayHandle } from './PdfDisplay'
import { BlockContentDisplay } from './BlockContentDisplay'

interface PDFEditorProps {
  storageRef: string
  signatories: Record<string, DocumentSignatory>
  signatoryColors: Record<string, string>
  blocks: DocumentSigningTemplateBlock[]
  onBlocksChange: (blocks: DocumentSigningTemplateBlock[]) => void
  onPageDimensionsChange: (
    pageDimensions: Array<{ width: number; height: number }>,
  ) => void
}

export const PdfEditor: React.FC<PDFEditorProps> = ({
  storageRef,
  signatories,
  signatoryColors,
  blocks,
  onBlocksChange,
  onPageDimensionsChange,
}) => {
  const [scale, setScale] = useState(1)
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null)
  const pdfDisplayRef = useRef<PdfDisplayHandle>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [pendingBlock, setPendingBlock] =
    useState<DocumentSigningTemplateBlock | null>(null)

  const [selectedSignatoryId, setSelectedSignatoryId] = useState<string | null>(
    Object.keys(signatories)[0] || null,
  )

  const displayBlocks = useMemo(
    (): BlockDisplay[] =>
      blocks.map((block) => ({
        id: block.id,
        type: block.type,
        color: signatoryColors[block.signatoryId],
        content: <BlockContentDisplay block={block} />,
        label: signatories[block.signatoryId]?.name || '',
        width: block.size.width,
        height: block.size.height,
        position: block.position,
        page: block.page,
      })),
    [blocks, signatoryColors, signatories],
  )

  const displayPendingBlock = useMemo(
    (): BlockDisplay | null =>
      pendingBlock && selectedSignatoryId
        ? {
            id: pendingBlock.id,
            type: pendingBlock.type,
            color: signatoryColors[selectedSignatoryId],
            content: <BlockContentDisplay block={pendingBlock} />,
            label: signatories[selectedSignatoryId]?.name || '',
            width: pendingBlock.size.width,
            height: pendingBlock.size.height,
            position: pendingBlock.position,
            page: pendingBlock.page,
          }
        : null,
    [pendingBlock, selectedSignatoryId, signatoryColors, signatories],
  )

  const handleAddBlock = (type: DocumentSigningTemplateBlockType) => {
    if (!selectedSignatoryId) {
      alert('Please select a signatory first')
      return
    }

    const newBlock: DocumentSigningTemplateBlock = {
      id: crypto.randomUUID(),
      type,
      position: {
        x: 0,
        y: 0,
      },
      size: {
        width: 100,
        height: 30,
      },
      page: 1,
      signatoryId: selectedSignatoryId,
    }
    setPendingBlock(newBlock)
  }

  const handleDeleteBlock = useCallback(
    (blockId: string) => {
      onBlocksChange(blocks.filter((block) => block.id !== blockId))
      if (selectedBlockId === blockId) {
        setSelectedBlockId(null)
      }
    },
    [selectedBlockId, blocks, onBlocksChange],
  )

  const scrollToBlock = (blockId: string) => {
    pdfDisplayRef.current?.scrollToBlock(blockId)
    setSelectedBlockId(blockId)
  }

  const handleZoomIn = () => {
    setScale((prev) => prev + 0.1)
  }

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.1, 0.5))
  }

  const handleBlockClick = (blockId: string) => {
    setSelectedBlockId(blockId)
  }

  const handleBlockPlaced = (block: BlockDisplay) => {
    if (!pendingBlock) return
    const newBlock: DocumentSigningTemplateBlock = {
      id: block.id,
      type: pendingBlock.type,
      position: block.position,
      size: {
        width: block.width || 100,
        height: block.height || 30,
      },
      page: block.page,
      signatoryId: selectedSignatoryId!,
    }

    onBlocksChange([...blocks, newBlock])
    setPendingBlock(null)
    setSelectedBlockId(block.id)
  }

  const handleBlockMoved = (
    blockId: string,
    newPosition: { x: number; y: number; page: number },
  ) => {
    onBlocksChange(
      blocks.map((block) =>
        block.id === blockId ? { ...block, position: newPosition } : block,
      ),
    )
  }

  const handleBlockResized = (
    blockId: string,
    newSize: { width: number; height: number },
  ) => {
    onBlocksChange(
      blocks.map((block) =>
        block.id === blockId
          ? {
              ...block,
              size: newSize,
            }
          : block,
      ),
    )
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Backspace' && selectedBlockId) {
        handleDeleteBlock(selectedBlockId)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedBlockId, handleDeleteBlock])

  return (
    <div className="flex">
      <div className="w-64 bg-gray-50 p-4 border-r border-gray-200">
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-2">Zoom</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleZoomOut}
              className="p-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center justify-center"
              title="Zoom Out"
            >
              <MagnifyingGlassMinusIcon className="h-5 w-5" />
            </button>
            <span className="text-sm font-medium min-w-[3rem] text-center">
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={handleZoomIn}
              className="p-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center justify-center"
              title="Zoom In"
            >
              <MagnifyingGlassPlusIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Signatory Selection */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-2">Select Signatory</h2>
          <div className="space-y-2">
            {Object.entries(signatories).map(([id, signatory]) => (
              <button
                key={id}
                onClick={() => setSelectedSignatoryId(id)}
                className={`w-full px-4 py-2 rounded text-left ${
                  selectedSignatoryId === id ? 'ring-2 ring-blue-500' : ''
                }`}
                style={{ backgroundColor: signatoryColors[id] }}
              >
                {signatory.name}
              </button>
            ))}
          </div>
        </div>

        <h2 className="text-lg font-semibold mb-4">Document Blocks</h2>

        <div className="space-y-2 mb-4">
          <button
            onClick={() =>
              handleAddBlock(DocumentSigningTemplateBlockType.SIGNATURE)
            }
            className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Add Signature
          </button>
          <button
            onClick={() =>
              handleAddBlock(DocumentSigningTemplateBlockType.DATE_SIGNED)
            }
            className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Add Date
          </button>
          <button
            onClick={() =>
              handleAddBlock(DocumentSigningTemplateBlockType.DATE_SIGNED_BOXES)
            }
            className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Add Date Boxes
          </button>
          <button
            onClick={() =>
              handleAddBlock(DocumentSigningTemplateBlockType.NAME)
            }
            className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Add Name
          </button>
        </div>

        {/* Block List */}
        <div className="space-y-2">
          {blocks.map((block) => (
            <div
              key={block.id}
              className={`p-2 border rounded cursor-pointer flex justify-between items-center ${
                selectedBlockId === block.id
                  ? 'ring-2 ring-blue-500'
                  : 'border-gray-200'
              }`}
              style={{ backgroundColor: signatoryColors[block.signatoryId] }}
              onClick={() => scrollToBlock(block.id)}
            >
              <div className="flex flex-col">
                <span className="capitalize">{block.type}</span>
                <span className="text-sm text-gray-500">
                  {signatories[block.signatoryId]?.name}
                </span>
                <span className="text-sm text-gray-500">Page {block.page}</span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleDeleteBlock(block.id)
                }}
                className="text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <div ref={containerRef} className="overflow-auto">
          <PdfDisplay
            ref={pdfDisplayRef}
            storageRef={storageRef}
            scale={scale}
            blocks={displayBlocks}
            selectedBlockId={selectedBlockId}
            pendingBlock={displayPendingBlock}
            onSelectBlock={handleBlockClick}
            onBlockPlaced={handleBlockPlaced}
            onBlockMoved={handleBlockMoved}
            onBlockResized={handleBlockResized}
            onPageDimensionsChange={onPageDimensionsChange}
          />
        </div>
      </div>
    </div>
  )
}
